<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { querystring, location, push } from 'svelte-spa-router';

	import { Alert, AlertV2, Container, Icon, Text, toast } from '@Shelly/shelly-design-system';

	import {
		accountHasCameraDevice,
		CAMERA_CLOUD_STORAGE_GRADIENT,
		CAMERA_CLOUD_STORAGE_GRADIENT_START,
		CAMERA_CLOUD_STORAGE_MISSING_INVOICE_KEY,
		CAMERA_CLOUD_STORAGE_PLANS_SECTION_ID,
		CAMERA_CLOUD_STORAGE_RETURN_PARAM,
		getSlotCountsFromSettings,
		hydrateFromUserSettings,
		refreshCameraNextBillingTime,
		refreshCameraSubscriptionFromSettings,
		subscribeCameraCloudStorage,
		upgradeCameraCloudStorage,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { scrollElementIntoView } from 'src/helpers/scroll_position_helpers';
	import { homes } from 'src/stores/account/home';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { capFirstChar } from 'src/helpers/utilities';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import type { CameraCloudStoragePlanTier } from 'src/types/CameraCloudStorage';
	import { CameraCloudStorageSubscriptionStatus } from 'src/types/CameraCloudStorage';
	import InvoiceDetailsModal from '../Premium/Billing/InvoiceDetailsModal.svelte';
	import ValidateEmailPromptModal from 'src/components/ValidateEmailPromptModal.svelte';

	import CameraCloudStorageActiveBanner from './CameraCloudStorageActiveBanner.svelte';
	import CameraCloudStorageBenefits from './CameraCloudStorageBenefits.svelte';
	import CameraCloudStoragePlansList from './CameraCloudStoragePlansList.svelte';
	import ManageCameraCloudStorageModal from './ManageCameraCloudStorageModal.svelte';
	import CameraCloudStorageSuccessfullySubscribed from './CameraCloudStorageSuccessfullySubscribed.svelte';
	import NoCameraDeviceModal from './NoCameraDeviceModal.svelte';

	/**
	 * Camera Cloud Storage (Add-on) section rendered inside the
	 * Subscriptions page. Mirrors the Figma layout — a purple-accented
	 * card with a section title, the "Camera Cloud Storage Benefit
	 * List" sub-section, and the "Choose Add On" plans grid.
	 *
	 * Subscribe / Upgrade clicks call BE-924 (`/api/subscribe[/...]`)
	 * and redirect to PayPal. After approval PayPal returns the user
	 * to `/settings/premium?ccs_state=1`; on return we refresh
	 * `user_settings` (per Spas's spec the `camera_subscription` and
	 * `camera_slots` blocks in `POST /user/get_settings` are the
	 * source of truth) and open the Successfully-subscribed →
	 * Assign-cameras wizard if the new subscription is active.
	 *
	 * Reactive `hydrateFromUserSettings` keeps the local
	 * `camera_cloud_storage` store aligned whenever `user_settings`
	 * changes.
	 */

	let loadingTier: CameraCloudStoragePlanTier | null = null;
	let showManageModal = false;
	let showPostSubscribeWizard = false;
	let postSubscribeOpenOnAdd = false;
	let showInvoiceDetailsModal = false;
	let showEmailValidationModal = false;
	let showNoCameraDeviceModal = false;

	$: isEmailValidated = $user_settings?.mail_validated === 1;

	/** Tier + action to retry after the user fills in invoice details. */
	let pendingInvoiceAction: {
		tier: CameraCloudStoragePlanTier;
		action: (t: CameraCloudStoragePlanTier) => ReturnType<typeof subscribeCameraCloudStorage>;
	} | null = null;

	$: hydrateFromUserSettings($user_settings);

	$: activePlan = $camera_cloud_storage.activePlan;
	$: isActivePlanActive = activePlan?.status === CameraCloudStorageSubscriptionStatus.Active;

	$: isPremium = ($user_data?.premium ?? 0) > 0;
	$: hasPremiumIncludedSlots = isPremium && !isActivePlanActive;
	$: isSharedHome = isActiveHomeShared($homes.list);
	$: canManageSlots = !isSharedHome;

	/**
	 * Tier we last asked `upgrade-preview` about — guards the reactive
	 * fetch so we hit the endpoint at most once per active plan.
	 */
	let lastBillingFetchTier: number | null = null;
	$: if (
		isActivePlanActive &&
		activePlan &&
		activePlan.tier !== lastBillingFetchTier &&
		!activePlan.nextBillingTime
	) {
		lastBillingFetchTier = activePlan.tier;
		refreshCameraNextBillingTime();
	}

	/** Prefer live slot assignments when `camera_slots.free` is stale (sub-units). */
	$: slotCounts = getSlotCountsFromSettings($user_settings);
	$: totalSlots = slotCounts.all;
	$: usedFromSettings = Math.max(0, slotCounts.all - slotCounts.free);
	$: usedFromAssignments = Object.values($camera_cloud_storage.slotAssignments ?? {}).filter(
		Boolean,
	).length;
	$: usedSlots = Math.max(usedFromSettings, usedFromAssignments);

	onMount(async () => {
		/**
		 * PayPal appends `?state=1` after our query string (e.g.
		 * `?ccs_state=1?state=1`). Normalise extra `?` to `&` so
		 * `URLSearchParams` reads `ccs_state` correctly.
		 */
		const normalisedQuery = ($querystring ?? '').replace(/\?/g, '&').replace(/^&+/, '');
		const params = normalisedQuery ? new URLSearchParams(normalisedQuery) : null;
		const returnedFromPaypal = params?.get(CAMERA_CLOUD_STORAGE_RETURN_PARAM) === '1';

		if (returnedFromPaypal) {
			await refreshCameraSubscriptionFromSettings();

			const current = $camera_cloud_storage.activePlan;
			if (current && current.status === CameraCloudStorageSubscriptionStatus.Active) {
				showPostSubscribeWizard = true;
			}
			push($location);
		}
	});

	async function handleSubscribe(tier: CameraCloudStoragePlanTier) {
		if (!isEmailValidated) {
			showEmailValidationModal = true;
			return;
		}
		if (!accountHasCameraDevice()) {
			showNoCameraDeviceModal = true;
			return;
		}
		await runSubscribeFlow(tier, subscribeCameraCloudStorage);
	}

	async function handleUpgrade(tier: CameraCloudStoragePlanTier) {
		if (!isEmailValidated) {
			showEmailValidationModal = true;
			return;
		}
		if (!accountHasCameraDevice()) {
			showNoCameraDeviceModal = true;
			return;
		}
		await runSubscribeFlow(tier, upgradeCameraCloudStorage);
	}

	async function runSubscribeFlow(
		tier: CameraCloudStoragePlanTier,
		action: (t: CameraCloudStoragePlanTier) => ReturnType<typeof subscribeCameraCloudStorage>,
	) {
		loadingTier = tier;
		try {
			const result = await action(tier);
			if (result.error) {
				if (result.error.key === CAMERA_CLOUD_STORAGE_MISSING_INVOICE_KEY) {
					pendingInvoiceAction = { tier, action };
					showInvoiceDetailsModal = true;
					return;
				}
				toast.open({
					type: 'danger',
					title: $format(result.error.key, { default: result.error.default }),
				});
				return;
			}
		} catch (error) {
			toast.open({
				type: 'danger',
				title: $format('toast_camera_cloud_storage_subscribe_failed', {
					default: 'Failed to subscribe to Camera Cloud Storage.',
				}),
			});
			console.error('[camera-cloud-storage] subscribe error', error);
		} finally {
			loadingTier = null;
		}
	}

	/** Called after the user saves invoice details — retries the original subscribe/upgrade action. */
	function onInvoiceDetailsSaved() {
		showInvoiceDetailsModal = false;
		if (pendingInvoiceAction) {
			const { tier, action } = pendingInvoiceAction;
			pendingInvoiceAction = null;
			runSubscribeFlow(tier, action);
		}
	}

	function handleAssignCamerasFromWizard() {
		showPostSubscribeWizard = false;
		showManageModal = true;
		postSubscribeOpenOnAdd = true;
	}

	function handleCloseWizard() {
		showPostSubscribeWizard = false;
		postSubscribeOpenOnAdd = false;
	}

	function handleManageModalClose() {
		showManageModal = false;
		postSubscribeOpenOnAdd = false;
	}
</script>

<section class="ccs-section mb-s mt-s" style={`--ccs-accent: ${CAMERA_CLOUD_STORAGE_GRADIENT};`}>
	<div class="d-flex align-items-center gap-s">
		<Icon
			size="s"
			icon="fas fa-camera-web"
			style={`color: ${CAMERA_CLOUD_STORAGE_GRADIENT_START};`}
		/>
		<Text styleType="h1">
			{#if activePlan && isActivePlanActive}
				{$format('text_title_camera_cloud_storage_add_on', {
					default: 'Camera Cloud Storage Add On',
				})}
			{:else}
				{$format('text_title_subscribe_for_camera_cloud_storage', {
					default: 'Subscribe for Camera Cloud Storage',
				})}
			{/if}
		</Text>
	</div>
	{#if canManageSlots}
		{#if activePlan && isActivePlanActive}
			<CameraCloudStorageActiveBanner
				{activePlan}
				{usedSlots}
				{totalSlots}
				on:openStorageSettings={() => (showManageModal = true)}
			/>
		{:else if hasPremiumIncludedSlots}
			<CameraCloudStorageActiveBanner
				variant="premium-included"
				{usedSlots}
				{totalSlots}
				on:openStorageSettings={() => (showManageModal = true)}
			/>
		{/if}
	{/if}
</section>

<Container class="mb-s width-full" noPadding>
	<Text element="h1" class="mb-m">
		{$format('text_camera_cloud_storage_benefit_list_title', {
			default: 'Camera Cloud Storage Benefit List',
		})}
	</Text>
	<CameraCloudStorageBenefits />
</Container>

<div id={CAMERA_CLOUD_STORAGE_PLANS_SECTION_ID}>
	<Container class="mb-s width-full" noPadding>
		<Text element="h2" class="mb-s">
			{$format('text_choose_camera_cloud_storage_add_on', {
				default: 'Choose Add On',
			})}
		</Text>
		{#if isSharedHome}
			{#if $showNewLayout}
				<AlertV2
					class="mb-s"
					heading={$format('shared_home_subscription_managed_by_owner', {
						default: 'Managed by the home owner',
					})}
					message={$format('shared_home_camera_cloud_storage_managed_by_owner_desc', {
						default:
							'Camera Cloud Storage for this home is managed by its owner. To change the plan, contact the home owner.',
					})}
				/>
			{:else}
				<Alert
					type="info"
					class="mb-s"
					heading={$format('shared_home_subscription_managed_by_owner', {
						default: 'Managed by the home owner',
					})}
					message={$format('shared_home_camera_cloud_storage_managed_by_owner_desc', {
						default:
							'Camera Cloud Storage for this home is managed by its owner. To change the plan, contact the home owner.',
					})}
					isHeadingBold
				/>
			{/if}
		{/if}
		<CameraCloudStoragePlansList
			{activePlan}
			{loadingTier}
			on:subscribe={(e) => handleSubscribe(e.detail.tier)}
			on:upgrade={(e) => handleUpgrade(e.detail.tier)}
		/>
	</Container>
</div>

{#if showPostSubscribeWizard && activePlan}
	<div class="ccs-success-overlay">
		<div class="ccs-success-card">
			<CameraCloudStorageSuccessfullySubscribed
				tier={activePlan.tier}
				on:click={handleAssignCamerasFromWizard}
			/>
			<div class="d-flex justify-content-center mt-m">
				<button class="ccs-skip-button" on:click={handleCloseWizard} type="button">
					{$format('button_camera_cloud_storage_skip_assignment', {
						default: 'Skip for now',
					})}
				</button>
			</div>
		</div>
	</div>
{/if}

{#if showManageModal && canManageSlots && (activePlan || isPremium)}
	<ManageCameraCloudStorageModal
		bind:showModal={showManageModal}
		{activePlan}
		openOnAddStep={postSubscribeOpenOnAdd}
		on:close={handleManageModalClose}
		on:upgrade={() => {
			showManageModal = false;
			scrollElementIntoView(CAMERA_CLOUD_STORAGE_PLANS_SECTION_ID);
		}}
	/>
{/if}

{#if showEmailValidationModal}
	<ValidateEmailPromptModal
		bind:showModal={showEmailValidationModal}
		on:close={() => (showEmailValidationModal = false)}
		customAlertHeading={capFirstChar(
			$format('alert_verify_email_from_camera_cloud_storage', {
				default:
					'To enable Camera Cloud Storage subscription, you need to confirm your e-mail is active',
			}),
		)}
	/>
{/if}

{#if showNoCameraDeviceModal}
	<NoCameraDeviceModal
		bind:showModal={showNoCameraDeviceModal}
		on:close={() => (showNoCameraDeviceModal = false)}
	/>
{/if}

{#if showInvoiceDetailsModal}
	<InvoiceDetailsModal
		bind:showModal={showInvoiceDetailsModal}
		callback={onInvoiceDetailsSaved}
		initial={true}
		title={capFirstChar(
			$format('modal_title_subscription_details', {
				default: 'subscription details',
			}),
		)}
	/>
{/if}

<style>
	.ccs-section {
		position: relative;
		background-color: var(--color-base);
		border-radius: var(--radius-big, 12px);
		overflow: hidden;
		padding: 24px;
		width: 100%;
	}

	.ccs-section::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 8px;
		background: var(--ccs-accent);
		border-top-left-radius: var(--radius-big, 12px);
		border-top-right-radius: var(--radius-big, 12px);
	}

	@media screen and (max-width: 639px) {
		.ccs-section {
			padding: 24px 16px 16px;
		}
	}

	.ccs-success-overlay {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.6);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
		padding: 16px;
	}

	.ccs-success-card {
		background: var(--color-base);
		border: 1px solid var(--color-2);
		border-radius: 16px;
		padding: 24px;
		max-width: 480px;
		width: 100%;
	}

	.ccs-skip-button {
		background: transparent;
		border: none;
		color: var(--color-3);
		cursor: pointer;
		font-size: 13px;
		text-decoration: underline;
	}

	.ccs-skip-button:hover {
		color: var(--color-white);
	}
</style>
